/** Auth state store using Zustand. */

import { create } from 'zustand'
import * as api from '../api/client'
import { useTradingStore } from './trading'
import { wsClient } from '../ws'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  /** Set when login requires TOTP step */
  pendingTotpToken: string | null

  login: (password: string) => Promise<void>
  verifyTotp: (code: string) => Promise<void>
  logout: () => void
  initialize: () => void
}

// Eagerly load tokens so the initial render sees isAuthenticated correctly.
// This avoids a flash-redirect to /login when tokens are already in localStorage.
api.loadTokens()
const _initiallyAuthed = api.hasTokens()

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: _initiallyAuthed,
  isLoading: false,
  error: null,
  pendingTotpToken: null,

  login: async (password: string) => {
    set({ isLoading: true, error: null })
    try {
      const result = await api.login(password)
      if (result.requires_totp) {
        set({
          isLoading: false,
          pendingTotpToken: result.partial_token,
        })
      } else {
        set({
          isAuthenticated: true,
          isLoading: false,
          pendingTotpToken: null,
        })
      }
    } catch (e) {
      set({
        isLoading: false,
        error: e instanceof Error ? e.message : 'Login failed',
      })
    }
  },

  verifyTotp: async (code: string) => {
    const { pendingTotpToken } = get()
    if (!pendingTotpToken) {
      set({ error: 'No pending TOTP verification' })
      return
    }

    set({ isLoading: true, error: null })
    try {
      await api.verifyTotp(pendingTotpToken, code)
      set({
        isAuthenticated: true,
        isLoading: false,
        pendingTotpToken: null,
      })
    } catch (e) {
      set({
        isLoading: false,
        error: e instanceof Error ? e.message : 'TOTP verification failed',
      })
    }
  },

  logout: () => {
    void api.logout().catch(() => undefined)
    api.clearTokens()
    wsClient.disconnect()
    useTradingStore.getState().reset()
    set({
      isAuthenticated: false,
      pendingTotpToken: null,
      error: null,
    })
  },

  initialize: () => {
    api.loadTokens()
    if (api.hasTokens()) {
      set({ isAuthenticated: true })
    }
  },
}))
